import React from 'react';
import PropTypes from 'prop-types';
import block from 'bem-cn-lite';
import _ from 'lodash';
import '@doc-tools/transform/dist/js/yfm';

import {compose} from 'recompose';
import withLocale from 'hoc/withLocale';
import withTranslation from 'hoc/withTranslation';

import Highlight from 'components/Highlight/Highlight';
import CollapsibleList from 'components/CollapsibleList/CollapsibleList';
import TranslateDemoForm from 'components/TranslateDemo/TranslateDemoForm/TranslateDemoForm';
import TranslateDemoError from 'components/TranslateDemo/TranslateDemoError/TranslateDemoError';
import {listLanguages, translate, detect} from 'components/TranslateDemo/api';

import 'styles/yfm.scss';
import './TranslateDemo.scss';

const TEXT_MAX_SIZE = 10000;
const REQUEST_DELAY_TIME = 400;
const DEFAULT_TO_LANG = 'en';
const b = block('TranslateDemo');

class TranslateDemo extends React.Component {
    static propTypes = {
        isConstructorVersion: PropTypes.bool,
        locale: PropTypes.bool,
        i18nK: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.translateWithDetect = _.debounce(async () => {
            await this.detect();
            await this.translate();
        }, REQUEST_DELAY_TIME);
        this.handleFrom = this.handleSelectChange('fromLang');
        this.handleTo = this.handleSelectChange('toLang');
    }

    state = {
        languages: [],
        text: '',
        translatedText: '',
        fromLang: '',
        toLang: DEFAULT_TO_LANG,
        errorText: '',
        isInit: true,
        languagesError: false,
        isFromLangSet: false,
        showLoader: false,
        isTranslated: false,
        // флаг нужен для правильного подсчета высоты секции с кодом запроса при анимации скрытия/показа
        isPreparing: true,
    };

    componentDidMount = async () => {
        await this.languages();
        this.setState({isPreparing: false});
    };

    componentDidUpdate(prevProps, prevState) {
        const {text, fromLang, toLang} = this.state;
        const isTextUpdated = text && text !== prevState.text;
        const isLangUpdated = fromLang !== prevState.fromLang || toLang !== prevState.toLang;
        const isFromExchange = text === prevState.translatedText;

        if (isTextUpdated && !isFromExchange) {
            this.translateWithDetect();
        } else if (isLangUpdated) {
            this.translate();
        }

        if (!text && text !== prevState.text) {
            this.setState({isTranslated: false});
        }
    }

    handleSelectChange = (input) => (value) => {
        this.setState({
            [input]: value,
            isFromLangSet: input === 'fromLang',
        });
    };

    handleTextChange = (value) => {
        this.setState({
            text: value.length < TEXT_MAX_SIZE ? value : this.state.text,
            translatedText: value.length ? this.state.translatedText : '',
            isInit: false,
        });
    };

    validateLangCode = (code) => {
        return this.state.languages.map((lang) => lang.value).includes(code);
    };

    languages = async () => {
        try {
            const {locale} = this.props;
            const result = await listLanguages(locale);
            const languages = result.map(({code, name}) => ({
                value: code,
                title: name,
                key: code,
            }));
            this.setState({languages});
        } catch (err) {
            this.setState({languagesError: true});
        }
    };

    detect = async () => {
        const {i18nK} = this.props;
        const {isFromLangSet, text, fromLang} = this.state;

        if (isFromLangSet || !text) {
            return;
        } else {
            try {
                this.setState({isAutoDetecting: true, isTranslated: false});
                const params = {text: this.state.text.slice(0, 1000)};
                if (fromLang) {
                    params.languageCodeHints = [fromLang];
                }

                const languageCode = await detect(params);

                if (languageCode && this.validateLangCode(languageCode)) {
                    this.setState({fromLang: languageCode});
                }
            } catch (err) {
                this.setState({errorText: i18nK('error-detect')});
            } finally {
                this.setState({isAutoDetecting: false});
            }
        }
    };

    translate = async () => {
        const {i18nK} = this.props;
        const {fromLang, toLang, text} = this.state;

        if (!text) {
            this.setState({translatedText: '', isTranslated: false});

            return;
        }

        const tId = setTimeout(() => this.setState({showLoader: true}), REQUEST_DELAY_TIME);

        try {
            this.setState({isTranslated: false, errorText: ''});
            const result = await translate({
                sourceLanguageCode: fromLang || 'ru',
                targetLanguageCode: toLang || 'en',
                texts: [text],
            });

            const translatedText = (result && result.length && result[0].text) || '';
            this.setState({translatedText, isInit: false, isTranslated: true});
        } catch (err) {
            this.setState({errorText: i18nK('error-translate')});
        } finally {
            clearTimeout(tId);
            this.setState({showLoader: false});
        }
    };

    handleExchange = () => {
        const {text, fromLang, toLang, translatedText} = this.state;

        if (text && fromLang && toLang) {
            this.setState({
                fromLang: toLang,
                toLang: fromLang,
                text: translatedText,
            });
        }
    };

    getLangsHtml() {
        const {i18nK} = this.props;
        const {languages} = this.state;
        let content = null;

        if (this.state.languagesError) {
            content = (
                <TranslateDemoError handleClick={this.languages}>
                    {i18nK('error-languages')}
                </TranslateDemoError>
            );
        } else {
            content = (
                <div title={i18nK('supportetd_languages-title')} className={b('table')}>
                    {languages.map((l) => (
                        <div key={l.value} className={b('cell')}>
                            {l.title}
                        </div>
                    ))}
                </div>
            );
        }

        return <div title={i18nK('supportetd_languages-title')}>{content}</div>;
    }

    getRequestHtml() {
        const {i18nK} = this.props;
        const params = this.state.text
            ? {
                  sourceLanguageCode: this.state.fromLang,
                  targetLanguageCode: this.state.toLang,
                  texts: [this.state.text],
              }
            : {
                  sourceLanguageCode: 'string',
                  targetLanguageCode: 'string',
                  texts: ['string'],
              };

        return (
            <section title={i18nK('show_code-title')} className={`${b('requset-section')} yfm`}>
                <h2>{i18nK('show_code-url_title')}</h2>
                <pre>
                    <code>{i18nK('show_code-url')}</code>
                </pre>
                <h2>{i18nK('show_code-params_title')}</h2>
                <Highlight languages={['json']}>{JSON.stringify(params, null, 4)}</Highlight>
                <a target="_blank" rel="noopener noreferrer" href={i18nK('show_code-link-url')}>
                    {i18nK('show_code-link')}
                </a>
            </section>
        );
    }

    render() {
        const {isConstructorVersion, i18nK} = this.props;
        const {
            languages,
            fromLang,
            toLang,
            text,
            isInit,
            translatedText,
            isAutoDetecting,
            showLoader,
            errorText,
            isTranslated,
            languagesError,
        } = this.state;

        return (
            <div className={b({constructor: isConstructorVersion})}>
                <h2 className={b('title')}>{i18nK('title')}</h2>
                <TranslateDemoForm
                    className={b('form')}
                    handleTextChange={this.handleTextChange}
                    handleFrom={this.handleFrom}
                    handleTo={this.handleTo}
                    handleExchange={this.handleExchange}
                    text={text}
                    fromLang={fromLang}
                    toLang={toLang}
                    translatedText={translatedText}
                    languages={languages}
                    errorText={errorText}
                    isInit={isInit}
                    isAutoDetecting={isAutoDetecting}
                    showLoader={showLoader}
                    isTranslated={isTranslated}
                    onTranslateError={this.translateWithDetect}
                />
                {(languages.length > 0 || languagesError) && (
                    <CollapsibleList arrowPosition="right">
                        {this.getLangsHtml()}
                        {(!this.state.isInit || this.state.isPreparing) && this.getRequestHtml()}
                    </CollapsibleList>
                )}
            </div>
        );
    }
}

export default compose(withLocale, withTranslation('translate-demo'))(TranslateDemo);
